import { knoxRootFromHostname } from '@/shared/cell-session';
import type { SessionPolicy } from './types';

/** Commercial / govcloud idle policy. */
export const DEFAULT_POLICY: SessionPolicy = {
    idleMinutes: 30,
    warningMinutes: 5,
};

/** Knox (CJIS / FedRAMP): lock and terminate at 15 minutes (AC-11 / AC-12). */
export const KNOX_POLICY: SessionPolicy = {
    idleMinutes: 15,
    warningMinutes: 5,
};

function normalizeHostname(hostOrUrl: string): string {
    return hostOrUrl.trim().replace(/^https?:\/\//i, '').split('/')[0]?.split(':')[0] ?? '';
}

/**
 * Every hostname a server request claims for itself: the ALB-forwarded value and
 * the Host header. Both are candidates rather than a preference order because
 * both are attacker-supplied — see {@link getPolicyForRequest}.
 */
function hostnamesFromRequest(request: { headers: Headers } | undefined): string[] {
    if (!request) return [];
    return [
        request.headers.get('x-forwarded-host')?.split(',')[0],
        request.headers.get('host'),
    ]
        .map((value) => normalizeHostname(value ?? ''))
        .filter((value) => value !== '');
}

function runtimeHostname(): string {
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return window.location.hostname;
    }
    const origin = process.env.AMPLIFY_APP_ORIGIN;
    if (origin) {
        try {
            return new URL(origin.includes('://') ? origin : `https://${origin}`).hostname;
        } catch {
            return normalizeHostname(origin);
        }
    }
    return process.env.API_DOMAIN_SUFFIX ?? '';
}

/** True when *any* candidate hostname is Knox public DNS — see {@link getPolicyForRequest}. */
function anyKnoxHost(hosts: (string | undefined)[]): boolean {
    return hosts.some((host) => !!host && knoxRootFromHostname(host) !== null);
}

/**
 * Idle policy for this page. Knox public DNS (`*.knox.{root}`) is 15 minutes;
 * everything else stays 30. `hostname` is a test seam; production callers omit it
 * and we read the page host (browser) or the deploy origin (server).
 */
export function getPolicy(hostname?: string): SessionPolicy {
    return anyKnoxHost([hostname, runtimeHostname()]) ? KNOX_POLICY : DEFAULT_POLICY;
}

/**
 * Idle policy for a server request (proxy, `/api/init`, `/api/session/extend`).
 *
 * Fails **safe**, not open. Both `x-forwarded-host` and `host` are client-supplied
 * — the ALB forwards `x-forwarded-host` untouched — so picking one and trusting it
 * would let a Knox caller send a commercial-looking host and downgrade itself from
 * the CJIS 15-minute clock (AC-11 / AC-12) to 30 minutes. Instead we union the
 * request hosts with the server-side deploy origin and take the stricter result: a
 * spoofed header can only tighten the policy, and a missing or placeholder
 * AMPLIFY_APP_ORIGIN still cannot keep Knox on the commercial clock.
 */
export function getPolicyForRequest(request: { headers: Headers } | undefined): SessionPolicy {
    const hosts = [...hostnamesFromRequest(request), runtimeHostname()];
    return anyKnoxHost(hosts) ? KNOX_POLICY : DEFAULT_POLICY;
}
